import { HeartPulse, ShieldAlert, ShieldCheck, Skull, Thermometer, TrendingDown, TrendingUp } from "lucide-react";
import { fmt } from "../lib/constants";
import { fmtDelta, healthLevel } from "../lib/hygiene";

const pill = "inline-flex items-center gap-1.5 sm:gap-2 rounded-full border px-2.5 py-1.5 sm:px-3 sm:py-2 tv:px-[16px] tv:py-[10px] text-xs tv:text-[16px] font-semibold shadow-sm transition-colors shrink-0";
const ICON = "w-4 h-4 tv:w-[21px] tv:h-[21px]";

// pill look per health level – poor = amber thermometer, critical = red pulsing skull ("the CRM is flatlining")
const LOOK = {
  clean: { box: "border-live/40 bg-live/5 text-live", icon: "text-live", Icon: ShieldCheck },
  ok: { box: "border-panel-border bg-panel text-ink hover:border-brand/60", icon: "text-brand", Icon: ShieldAlert },
  poor: { box: "border-amber-400 bg-amber-100 text-amber-900 hover:border-amber-500", icon: "text-amber-700 hygiene-poor-icon", Icon: Thermometer },
  critical: { box: "border-brand bg-brand/10 text-brand hover:bg-brand/15 hygiene-critical", icon: "text-brand animate-pulse", Icon: Skull },
  unknown: { box: "border-panel-border bg-panel text-slate-500", icon: "text-slate-400", Icon: HeartPulse },
};

// Top-bar headline of the CRM hygiene audit (staff only – never on display screens): open issues + change since yesterday
export const HygienePill = ({ summary, onClick }) => {
  if (!summary) return null;
  const broken = !!summary.error;
  const total = summary.total ?? 0;
  const health = healthLevel(total, broken);
  const look = LOOK[health.level];
  const delta = summary.delta_yesterday;
  const deltaText = fmtDelta(delta);
  const title = broken
    ? `CRM hygiene could not be computed on the last refresh: ${summary.error}`
    : `CRM hygiene ${health.label.toLowerCase()}: ${fmt(total)} open ${total === 1 ? "issue" : "issues"} on ${fmt(summary.records || 0)} HubSpot records since 1 Oct${deltaText ? ` · ${deltaText} since yesterday` : ""}${summary.fixed_7d ? ` · ${fmt(summary.fixed_7d)} fixed in 7 days` : ""} – click for details`;
  const Icon = look.Icon;
  return (
    <button
      type="button"
      data-testid="hygiene-pill"
      data-health={health.level}
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`${pill} ${look.box}`}
    >
      <Icon className={`${ICON} ${look.icon}`} data-testid="hygiene-pill-health" />
      <span className="hidden sm:inline tv:hidden font-semibold opacity-70">{health.level === "poor" || health.level === "critical" ? health.label : "CRM"}</span>
      <span className="num font-bold" data-testid="hygiene-pill-total">{broken ? "n/a" : fmt(total)}</span>
      {deltaText && !broken && (
        <span
          data-testid="hygiene-pill-delta"
          className={`num inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] tv:text-[13px] ${delta > 0 ? "bg-brand/10 text-brand" : delta < 0 ? "bg-live/10 text-live" : "bg-slate-100 text-slate-500"}`}
          title="Change since the first check of yesterday"
        >
          {delta > 0 ? <TrendingUp className="w-3 h-3 tv:w-[14px] tv:h-[14px]" /> : delta < 0 ? <TrendingDown className="w-3 h-3 tv:w-[14px] tv:h-[14px]" /> : null}
          {deltaText}
        </span>
      )}
    </button>
  );
};

// Health badge for the panel header ("Poor" thermometer / "Critical" skull / "Clean")
export const HygieneHealthBadge = ({ total, broken = false }) => {
  const health = healthLevel(total, broken);
  if (health.level === "ok" || health.level === "unknown") return null;
  const look = LOOK[health.level];
  const Icon = look.Icon;
  return (
    <span data-testid="hygiene-panel-health" data-health={health.level} title={health.text} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${look.box}`}>
      <Icon className={`w-3.5 h-3.5 ${look.icon}`} /> {health.label}
    </span>
  );
};
